import { CloseRounded, SearchRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Typography,
  Checkbox,
  CardActions,
  TablePagination,
  Divider,
  styled,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { Scheme } from "../../../schemes/models/schemes";
import { getSchemes } from "../../../../services/schemes/schemes";
import Loading from "../../../../components/loading/loading";
import { greyEnabled, purple, purpleLight, black } from "../../../../theme/colors";

interface MapSchemePopupInterface {
  openModel: boolean;
  setOpenModal: (val: boolean) => void;
  setSelectedSchemeList: (item: Scheme[] | null) => void;
  selectedSchemeList: Scheme[];
}

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function MapSchemePopup({
  openModel,
  setOpenModal,
  setSelectedSchemeList,
  selectedSchemeList,
}: MapSchemePopupInterface) {
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [__selectedSchemeList, __setSelectedSchemeList] = useState<
    Scheme[] | null
  >([]);

  const areAllCurrentPageSchemesSelected = () => {
    return (
      schemes?.list?.length > 0 &&
      schemes?.list?.every((item: Scheme) =>
        __selectedSchemeList?.some(
          (selectedItem) => selectedItem.schemeId === item.schemeId
        )
      )
    );
  };


  const handleSelectAll = () => {
    if (!schemes?.list) return;

    if (areAllCurrentPageSchemesSelected()) {
      const filteredList = __selectedSchemeList?.filter(
        (selectedItem) =>
          !schemes.list.some(
            (item: Scheme) => item.schemeId === selectedItem.schemeId
          )
      );
      __setSelectedSchemeList(filteredList ?? []);
    } else {
      const newItems = schemes.list.filter(
        (item: Scheme) =>
          !__selectedSchemeList?.some(
            (selectedItem) => selectedItem.schemeId === item.schemeId
          )
      );
      __setSelectedSchemeList([...(__selectedSchemeList ?? []), ...newItems]);
    }
  };



  const isSelected = (item: Scheme) => {
    return __selectedSchemeList?.some(
      (schemeList) => schemeList.schemeId === item.schemeId
    );
  };

  const handleCheckboxClick = (item: Scheme) => {
    let newList: any = [];
    if (isSelected(item)) {
      newList = __selectedSchemeList?.filter(
        (schemeList) => schemeList.schemeId !== item.schemeId
      );
    } else {
      newList = [...__selectedSchemeList!, item];
    }
    __setSelectedSchemeList(newList);
  };

  const { data: schemes, isLoading } = useQuery(
    ["schemes", searchValue, page],
    async () => {
      let payload = {
        page: page + 1,
        size: 8,
        keyword: searchValue,
      };
      let response = await getSchemes(payload);
      return response.data.data;
    }
  );

  useEffect(() => {
    if (openModel) {
      __setSelectedSchemeList(selectedSchemeList);
      setSearchValue("");
      setPage(0);
    }
  }, [openModel]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleClose = () => setOpenModal(false);

  return (
    <Dialog open={openModel} onClose={handleClose} PaperProps={{ sx: { borderRadius: 4 } }}>
      <Card
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: 560,
          maxWidth: "90vw",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
        >
          <Typography variant="h6" fontWeight={700} color={black}>
            Select Schemes to Map
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseRounded sx={{ color: greyEnabled }} />
          </IconButton>
        </Box>

        <CardContent sx={{ px: 3 }}>
          <Box>
            <TextField
              value={searchValue}
              onChange={(e) => {
                setPage(0);
                setSearchValue(e.target.value);
              }}
              size="small"
              fullWidth
              placeholder="Search by scheme name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded sx={{ color: greyEnabled, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {!isLoading && schemes?.list?.length > 0 && (
            <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1}>
              <Checkbox
                checked={areAllCurrentPageSchemesSelected()}
                onChange={handleSelectAll}
                sx={{ padding: 0, marginRight: 1 }}
              />
              <Button
                variant="text"
                onClick={handleSelectAll}
                sx={{ textTransform: "none", fontWeight: 600, color: purple }}
              >
                {areAllCurrentPageSchemesSelected() ? "Deselect All" : "Select All"}
              </Button>
            </Box>
          )}
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <Loading minHeight={0} />
            </Box>
          ) : !schemes?.list?.length ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={0.5}>
              <Typography fontWeight={600} color={black}>
                No schemes found
              </Typography>
              <Typography variant="body2" color={greyEnabled}>
                Try adjusting your search.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 300, overflow: "auto", mt: 1 }}>
              {schemes?.list?.map((item: Scheme) => {
                return (
                  <Box mt={2} key={item.schemeId}>
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      flexDirection={"row"}
                    >
                      <Checkbox
                        onChange={() => handleCheckboxClick(item)}
                        sx={{ color: purple }}
                        checked={
                          __selectedSchemeList?.filter(
                            (item2) => item2.schemeId === item.schemeId
                          ).length !== 0
                            ? true
                            : false
                        }
                      />
                      <Typography
                        variant="body1"
                        fontWeight={"600"}
                        textTransform={"capitalize"}
                        ml={2}
                      >
                        {item.name}
                      </Typography>
                    </Box>
                    <Box display={"flex"} flexDirection={'row'} ml={7} flexWrap="wrap" >
                      <Typography
                        variant="body2"
                        color={purple}
                        fontWeight={"600"}
                        ml={1}
                      >
                        {item.governmentType}
                      </Typography>

                      <Box display={"flex"} flexDirection={'row'} mb={1}>
                        {item?.categories?.map((cat: string) => {
                          return (
                            <Typography
                              key={cat}
                              color={purple}
                              variant="body2"
                              textTransform={"capitalize"}
                              ml={1}
                              mr={1}
                              fontWeight={"600"}
                            >
                              {cat}
                            </Typography>
                          );
                        })}
                      </Box>
                    </Box>
                    <Divider
                      sx={{
                        border: "1px dotted #7C797C",
                        opacity: 0.5,
                        mt: 2,
                        mx: 2,
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
        {!isLoading && schemes?.list?.length > 0 && (
          <CardActions sx={{ px: 1 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-end",
                width: "100%",
                mx: 2,
              }}
            >
              <TablePagination
                component="div"
                rowsPerPageOptions={[]}
                count={schemes?.total || 0}
                rowsPerPage={8}
                page={page || 0}
                onPageChange={handleChangePage}
              />
            </Box>
          </CardActions>
        )}
        <Box display="flex" justifyContent="space-between" gap={1.5} sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: purple,
              color: purple,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { borderColor: purple, bgcolor: purpleLight },
            }}
          >
            Cancel
          </Button>
          <Button
            sx={{
              minWidth: 200,
              bgcolor: purple,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#7A2074" },
            }}
            variant="contained"
            disableElevation
            onClick={() => {
              setSelectedSchemeList(__selectedSchemeList);
              handleClose();
            }}
          >
            Map Schemes
          </Button>
        </Box>
      </Card>
    </Dialog>
  );
}
